import { useState, useRef, useEffect } from 'react';
import SharedHeader from '../components/layout/SharedHeader';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDoc, updateDoc, doc, collection, addDoc, getDocs, query, orderBy, limit, arrayUnion, increment, where, writeBatch, setDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { preflightQuestions } from '../data/preflightQuestions';
import {
  Mic,
  MicOff,
  Send,
  Volume2,
  VolumeX,
  AlertCircle,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link, useNavigate } from 'react-router-dom';
import SubNavigation from '../components/layout/SubNavigation';
import chatService from '../services/chatService';
import PostflightQuestionnaire from '../components/PostflightQuestionnaire';
import FeedbackCTA from '../components/FeedbackCTA';
import UserSurvey from '../components/UserSurvey';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faMicrophoneSlash, faPaperPlane, faGear, faStop } from '@fortawesome/free-solid-svg-icons';
import RiskSection from '../components/RiskSection';
import { API_CONFIG, BASE_URL } from '../config/api/apiConfig';

// Add this utility function to extract flight dates from messages
const extractFlightDate = (message) => {
  // Basic regex patterns for date formats
  const patterns = [
    // Tomorrow at time
    {
      regex: /tomorrow\s+at\s+(\d{1,2}[:.]\d{2})/i,
      handler: (match) => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const [hours, minutes] = match[1].split(/[:.]/);
        tomorrow.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return tomorrow;
      }
    },
    // Specific date at time
    {
      regex: /(\d{1,2}[-/]\d{1,2}[-/]\d{2,4})\s+at\s+(\d{1,2}[:.]\d{2})/i,
      handler: (match) => {
        const [date, time] = match;
        const [hours, minutes] = time.split(/[:.]/);
        const dateObj = new Date(date);
        dateObj.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return dateObj;
      }
    },
    // Today at time
    {
      regex: /today\s+at\s+(\d{1,2}[:.]\d{2})/i,
      handler: (match) => {
        const today = new Date();
        const [hours, minutes] = match[1].split(/[:.]/);
        today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return today;
      }
    }
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern.regex);
    if (match) {
      try {
        const date = pattern.handler(match);
        console.log('Extracted flight date:', date);
        return date.toISOString();
      } catch (error) {
        console.error('Error parsing date:', error);
      }
    }
  }

  return null;
};

const EnhancedChatApp = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [idToken, setIdToken] = useState(null);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentResponses, setCurrentResponses] = useState([]);
  const [responses, setResponses] = useState({});
  const [textInput, setTextInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);
  
  const auth = getAuth();
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const audioContextRef = useRef(null);

  const [pilotId, setPilotId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [isQuestionnaire, setIsQuestionnaire] = useState(true);
  const [userProfilePic, setUserProfilePic] = useState(null);
  const [followUpIndex, setFollowUpIndex] = useState(null);

  const [user, setUser] = useState(null);
  const errorRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);

  const [sessionMetadata, setSessionMetadata] = useState({
    startTime: null,
    endTime: null,
    totalTokens: 0,
    status: 'active',
    threadId: null // Will be set when first message is sent
  });

  const [showPostflight, setShowPostflight] = useState(false);
  const [flightDate, setFlightDate] = useState(null);
  const [showSurvey, setShowSurvey] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [aiLoadingMessage, setAiLoadingMessage] = useState(null);

  const inputRef = useRef(null);

  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setIdToken(token);
          console.log('ID Token:', token);

          const uid = user.uid;
          console.log("User UID:", uid);

          const userDoc = doc(db, "pilot", uid);
          const userProfile = await getDoc(userDoc);

          if (userProfile.exists()) {
            console.log('User Profile Data:', userProfile.data());
            setUserProfile(userProfile.data());
          } else {
            console.warn('No user profile found for UID:', uid);
          }
        } catch (error) {
          console.error('Error fetching user profile or token:', error);
          setError('Failed to fetch user profile or token'); // Set error message
        }
      } else {
        console.warn('No user is currently signed in.');
        setUserProfile(null);
        setIdToken(null);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const fetchUserProfile = async () => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {

        try {
          const token = await user.getIdToken();
          setIdToken(token);
          console.log('ID Token:', token);
          
          const uid = user.uid;
          console.log("User UID:", uid);

          const userDoc = doc(db, "pilot", uid);
          const userProfile = await getDoc(userDoc);

          if (userProfile.exists()) {
            console.log('User Profile Data:', userProfile.data());
            setUserProfile(userProfile.data());
          } else {
            console.warn('No user profile found for UID:', uid);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        console.warn('No user is currently signed in.');
        setUserProfile(null);
        setIdToken(null);
      }
    });

    return () => unsubscribe();
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const initializeChatSession = async () => {
    try {
      if (!auth.currentUser) return;

      const sessionRef = collection(db, `pilot/${auth.currentUser.uid}/chatSessions`);
      const newSession = await addDoc(sessionRef, {
        metadata: {
          startTime: new Date().toISOString(),
          status: 'active',
          totalTokens: 0,
          threadId: null // Will be set when first message is sent
        }
      });

      setSessionId(newSession.id);
      console.log('Created new chat session:', newSession.id);
      return newSession.id;
    } catch (error) {
      console.error('Error creating chat session:', error);
      setError('Failed to initialize chat session');
    }
  };

  const onComplete = async (responses) => {
    try {
      if (!auth.currentUser || !sessionId) {
        console.error('No user or session ID available');
        return;
      }

      console.log('Received responses:', responses);

      // Validate responses before saving
      const validResponses = {};
      preflightQuestions.initial.forEach((question, index) => {
        // Use index to access response since responses are stored with numeric keys
        const response = responses[index];
        console.log(`Processing question ${index}:`, question.id, 'Response:', response);
        
        if (response != null && response !== '') {
          validResponses[question.id] = {
            questionId: question.id,
            value: response,
            timestamp: new Date().toISOString()
          };
        }
      });

      console.log('Validated responses:', validResponses);

      // Only proceed if we have valid responses
      if (Object.keys(validResponses).length > 0) {
        const sessionRef = doc(db, `pilot/${auth.currentUser.uid}/chatSessions/${sessionId}`);
        
        const responseObject = {
          status: 'completed',
          responses: validResponses,
          timestamp: new Date().toISOString(),
          completedAt: new Date().toISOString()
        };

        console.log('Saving response object:', responseObject);

        try {
          await updateDoc(sessionRef, {
            preflightCheck: responseObject
          });

          console.log('Saved preflight responses for session:', sessionId);
          setResponses(responses);
          setIsQuestionnaire(false);

          // Set initial message and focus input
          setInputMessage("How can I assist you with your flight planning?");
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              // Move cursor to end of text
              inputRef.current.setSelectionRange(
                inputRef.current.value.length,
                inputRef.current.value.length
              );
            }
          }, 100);

          setMessages(prev => [...prev, {
            id: Date.now(),
            text: "Thank you for completing the pre-flight questionnaire.",
            sender: 'assistant',
            timestamp: new Date().toISOString()
          }]);
        } catch (error) {
          console.error('Firestore update error:', error);
          setError('Failed to save preflight responses to database');
        }
      } else {
        console.warn('No valid responses to save. Responses received:', responses);
        setError('No valid responses to save. Please ensure all questions are answered.');
      }
    } catch (error) {
      console.error("Error in onComplete:", error);
      setError("Failed to process preflight responses. Please try again.");
    }
  };

  const handleAnswer = (answer) => {
    const newResponses = { ...responses, [currentQuestion]: answer.value };
    setResponses(newResponses);

    setMessages(prevMessages => [...prevMessages, {
      id: Date.now(),
      text: answer.text,
      sender: 'user',
      timestamp: new Date().toISOString()
    }]);

    const currentQuestionData = preflightQuestions.initial[currentQuestion];

    if (followUpIndex !== null) {
      // Handle follow-up questions
      const followUpQuestions = currentQuestionData.followUp.questions;
      if (followUpIndex < followUpQuestions.length - 1) {
        // Move to the next follow-up question
        setFollowUpIndex(followUpIndex + 1);
        addFollowUpQuestionToChat(currentQuestion, followUpIndex + 1);
      } else {
        // Reset follow-up index and move to the next main question
        setFollowUpIndex(null);
        moveToNextMainQuestion(newResponses);
      }
    } else if (answer.value === currentQuestionData.followUp?.condition) {
      // Start follow-up questions
      setFollowUpIndex(0);
      addFollowUpQuestionToChat(currentQuestion, 0);
    } else {
      // Move to the next main question
      moveToNextMainQuestion(newResponses);
    }
  };

  const addFollowUpQuestionToChat = (questionIndex, followUpIndex) => {
    const followUpQuestion = preflightQuestions.initial[questionIndex].followUp.questions[followUpIndex];
    if (!followUpQuestion) return;

    setMessages(prev => [...prev, {
      id: Date.now(),
      //text: `${followUpQuestion.prefix ? `${followUpQuestion.prefix}: ` : ''}${followUpQuestion.text}`,
      text: `${followUpQuestion.text}`,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      isQuestion: true,
      options: followUpQuestion.options
    }]);
  };

  const moveToNextMainQuestion = (newResponses) => {
    if (currentQuestion < preflightQuestions.initial.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      addQuestionToChat(currentQuestion + 1);
    } else {
      onComplete(newResponses);
    }
  };

  const addQuestionToChat = (questionIndex) => {
    const question = preflightQuestions.initial[questionIndex];
    if (!question) return;

    setMessages(prev => [...prev, {
      id: Date.now(),
      text: `${question.text}`,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      isQuestion: true,
      options: question.options
    }]);
  };

  // Audio functionality
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioAnalyserRef.current = analyser;
      audioContextRef.current = audioContext;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        // Handle the recorded audio blob here
        // You might want to send it to your chat service
      };
      
      setRecordingTime(0);
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Store timer reference
      timerRef.current = timer;
      console.log("Timer started");
      mediaRecorder.start();
      console.log("Media recorder started");
      setIsRecording(true);
      console.log("Is recording set to true");
            
      monitorAudioLevel();
      console.log("Audio level monitoring started");
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording. Please check your microphone permissions.');
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecordingTime(0);
    
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      
      try {
        const audioBlob = await new Promise((resolve) => {
          const chunks = [];
          mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
            chunks.push(event.data);
          });
          
          mediaRecorderRef.current.addEventListener('stop', () => {
            const blob = new Blob(chunks, { type: 'audio/webm' });
            resolve(blob);
          });
        });

        // Convert to base64
        const reader = new FileReader();
        const base64Audio = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(audioBlob);
        });

        // Send to OpenAI Whisper API
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aviationadvisors-uat-backend-769604873298.us-central1.run.app';
        const response = await fetch(`${API_BASE_URL}/api/chat/bypass`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            file: base64Audio,
            model: 'whisper-1',
          }),
        });

        const data = await response.json();
        if (data.text) {
          setInputMessage(data.text);
        }

      } catch (error) {
        console.error('Error processing audio:', error);
      } finally {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      }
    }
  };

  const monitorAudioLevel = () => {
    if (!audioAnalyserRef.current) return;
    
    const analyser = audioAnalyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const updateLevel = () => {
      if (!isRecording) return;
      
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average);
      
      requestAnimationFrame(updateLevel);
    };
    
    updateLevel();
  };

  const handleSpeakerToggle = () => {
    setIsSpeaking(!isSpeaking);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const messageToSend = inputMessage.trim();
    
    // Clear input immediately
    setInputMessage('');
    
    // Add user message to chat immediately
    setMessages(prevMessages => [...prevMessages, {
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    }]);

    try {
      setIsTyping(true);
      setError(null);

      // Get the current user's ID token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const idToken = await user.getIdToken();

      // Create the request payload
      const requestPayload = {

        message: messageToSend,
        config: {
          configurable: {
            user_id: user.uid,
            profile: userProfile
          }
        }
      };

      console.log('API URL:', `${BASE_URL}${API_CONFIG.ENDPOINTS.CHAT}`);
      console.log('Request Payload:', requestPayload);
      console.log('Stringified payload:', JSON.stringify(requestPayload));

      // Create a new EventSource for SSE
      const response = await fetch(`${BASE_URL}${API_CONFIG.ENDPOINTS.CHAT}`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({ message: messageToSend }) // Simplify the payload first to test
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          try {
            const jsonStr = line.replace(/^data: /, '');
            const data = JSON.parse(jsonStr);
            console.log('Received SSE data:', data);

            // Check if this is a message from the assistant
            if (data.type === 'message' && data.role === 'assistant') {
              // Try to parse nested event data
              const eventMatch = data.content.match(/event: values\r\ndata: (.*?)(?:\r\n|$)/);
              if (eventMatch) {
                // Parse the nested JSON data
                const nestedData = JSON.parse(eventMatch[1]);
                console.log('Parsed nested data:', nestedData);

                if (nestedData.messages) {
                  // Process the AI message with its metadata
                  const messageData = {
                    role: 'assistant',
                    content: nestedData.messages[0]?.content || '',
                    messageId: data.messageId,
                    details: {
                      agents: nestedData.agents || [],
                      metadata: nestedData.metadata || []
                    },
                    isPartial: false
                  };

                  onReceiveMessage(messageData);
                  setIsTyping(false);
                }
              } else {
                // Handle regular assistant messages (like questionnaire)
                onReceiveMessage({
                  role: 'assistant',
                  content: data.content,
                  messageId: data.messageId,
                  isPartial: false
                });
                setIsTyping(false);
              }
            }

            if (data.error) {
              console.error('Error:', data.error);
              setError(new Error(data.error));
              break;
            }
          } catch (error) {
            console.error('Error parsing SSE data:', error);
          }
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      setError(error);
    } finally {
      setIsTyping(false);
    }
  };

  const onReceiveMessage = (data) => {
    setMessages((prevMessages) => {
      if (!data?.content) return prevMessages;

      const lastMessage = prevMessages[prevMessages.length - 1];

      // If last message is an assistant partial, append new content
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isPartial) {

        return [
          ...prevMessages.slice(0, -1),
          {
            ...lastMessage,
            content: lastMessage.content + data.content,
            messageId: data.messageId,
            isPartial: true,
            timestamp: new Date().toISOString(),
            role: 'ai',
            sender: 'ai', // Ensure sender is set for MessageContent component
          }
        ];
      }
      // Otherwise, create a new assistant message
      return [
        ...prevMessages,
        {
          role: 'assistant',
          content: data.content,
          timestamp: new Date().toISOString(),
          messageId: data.messageId,
          isPartial: true,
        }
      ];
    });
  };

  // Add MessageContent component
  const MessageContent = ({ text, sender, userName, isTyping, message }) => {
    const navigate = useNavigate();
    
    if (sender === 'error') {
      return (
        <div className="flex justify-center my-2">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg max-w-[80%]">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4" />
              <span>{text}</span>
            </div>
          </div>
        </div>
      );
    }

    if (sender === 'assistant') {
      const messageContent = typeof text === 'string' ? text : (text?.content || message?.content || '');
      const isPartialMessage = message?.isPartial;

      return (
        <div className="flex space-x-4">
          <div className="flex-shrink-0">
            <img
              className="mt-6 drop-shadow-lg h-[80px] w-[80px] object-center object-cover rounded-t-xl"
              src="/assets/ai-chat-logo.png"
              alt="Assistant"
            />
          </div>
          <div className="flex flex-col space-y-1 max-w-[calc(100%-120px)]">
            <div className="leading-[normal] font-bold text-xs mb-3">
              Aviation Advisor AI
              {isPartialMessage && (
                <span className="ml-2 typing-indicator">thinking</span>
              )}
            </div>
            
            <div className={`p-4 rounded-tr-xl rounded-br-xl rounded-tl-xl overflow-hidden bg-[hsla(223,35%,97%,1)] text-[hsla(150,2%,25%,1)] ${isPartialMessage ? 'message-appear' : ''}`}>
              <ReactMarkdown
                className="prose max-w-none whitespace-pre-wrap break-words m-0"
                remarkPlugins={[remarkGfm]}
              >
                {messageContent}
              </ReactMarkdown>
              
              {message?.details && (
                <div className="mt-6 space-y-4">
                  {Object.keys(message.details).map((agent) => (
                    <RiskSection
                      key={agent}
                      agent={agent}
                      details={message.details}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <div className="leading-[normal] text-xs mt-3 text-[hsla(150,1%,72%,1)]">
              {new Date(message?.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      );
    }

    // User message component remains the same
    return (
      <div className="flex justify-end space-x-4">
        <div className="flex flex-col space-y-1 items-end">
          <div className="leading-[normal] font-bold text-xs mb-3">
            {userProfile?.fname} {userProfile?.lname}
          </div>
          <div className="p-4 rounded-tl-xl rounded-bl-xl rounded-tr-xl"
               style={{
                 backgroundColor: 'hsla(120, 73%, 91%, 1)',
                 color: 'hsla(120, 100%, 12%, 1)'
               }}>
            <ReactMarkdown
              className="prose max-w-none whitespace-pre-wrap break-words m-0"
              remarkPlugins={[remarkGfm]}
            >
              {typeof text === 'string' ? text : text?.content || ''}
            </ReactMarkdown>
          </div>
          <div className="leading-[normal] text-xs mt-3"
               style={{ color: 'hsla(150, 1%, 72%, 1)' }}>
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <div className="flex-shrink-0">
          <img
            className="mt-8 w-[55px] h-[55px] object-contain rounded-tl-xl rounded-tr-xl rounded-br-xl rounded-bl-none"
            src={userProfile?.photo_url || "/assets/generic-user-avatar.png"}
            alt="User"
          />
        </div>
      </div>
    );
  };

  const RiskSection = ({ agent, details }) => {
    const content = details[agent];
    if (!content) return null;

    return (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <h3 className="text-lg font-semibold capitalize mb-2">
          {agent === 'risk' ? 'Risk Assessment' : agent}
        </h3>
        {agent === 'risk' ? (
          <div>
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium
              ${content.level === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                content.level === 'red' ? 'bg-red-100 text-red-800' :
                'bg-green-100 text-green-800'}`}>
              Risk Level: {content.level?.toUpperCase()}
            </div>
            {content.category_risks && (
              <div className="mt-2 space-y-1">
                {Object.entries(content.category_risks)
                  .filter(([_, value]) => value !== null)
                  .map(([category, value]) => (
                    <div key={category} className="text-sm">
                      <span className="capitalize">{category.replace(/_/g, ' ')}:</span> {value}
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ) : (
          <ReactMarkdown
            className="prose max-w-none text-sm"
            remarkPlugins={[remarkGfm]}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
    );
  };

  // Add CSS for typing animation
  const styles = `
    .typing-indicator {
      display: inline-flex;
      align-items: center;
      color: #666;
      font-weight: normal;
      font-size: 0.75rem;
    }

    .typing-indicator::after {
      content: '...';
      display: inline-block;
      animation: ellipsis 1.5s infinite;
      width: 12px;
      text-align: left;
    }

    @keyframes ellipsis {
      0% { content: '.'; }
      33% { content: '..'; }
      66% { content: '...'; }
      100% { content: '.'; }
    }

    .message-appear {
      animation: fadeIn 0.3s ease-in;
    }

    @keyframes fadeIn {
      from { 
        opacity: 0;
        transform: translateY(5px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }

    .waveform-bar {
      animation: wave 0.5s ease-in-out infinite;
      transform-origin: bottom;
    }

    @keyframes wave {
      0% { transform: scaleY(0.2); }
      50% { transform: scaleY(1); }
      100% { transform: scaleY(0.2); }
    }
  `;

  useEffect(() => {
    if (isFirstMessage && isQuestionnaire) {
      setMessages([{
        id: Date.now(),
        text: "Welcome to the Aviation Assistant. Let's start with a few pre-flight questions to ensure your safety.",
        sender: 'assistant',
        timestamp: new Date().toISOString()
      }]);
      addQuestionToChat(0);
      setIsFirstMessage(false);
    }
  }, [isFirstMessage, isQuestionnaire]);

  useEffect(() => {
    // Scroll to the bottom whenever messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [error]);

  // Initialize chat session on component mount
  useEffect(() => {
    if (!auth.currentUser || isInitialized) return;
    
    const initSession = async () => {
      try {
        const newSessionId = await initializeChatSession();
        setSessionId(newSessionId);
        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing chat session:', error);
        setError('Failed to initialize chat session');
      }
    };

    initSession();
  }, [auth.currentUser, isInitialized, initializeChatSession]);

  useEffect(() => {
    const checkForPendingPostflight = async () => {
      if (!auth.currentUser) return;

      const sessionsRef = collection(db, `pilot/${auth.currentUser.uid}/chatSessions`);
      const q = query(
        sessionsRef,
        where('metadata.status', '==', 'active'),
        where('metadata.flightDate', '<=', new Date()),
        where('postflightCompleted', '==', false)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const session = querySnapshot.docs[0];
        setSessionId(session.id);
        setFlightDate(session.data().metadata.flightDate);
        setShowPostflight(true);
      }
    };

    checkForPendingPostflight();
  }, [auth.currentUser]);

  const handlePostflightComplete = async (responses) => {
    try {
      if (!auth.currentUser || !sessionId) {
        console.error('No user or session ID available');
        return;
      }

      const timestamp = new Date().toISOString();
      const pilotId = auth.currentUser.uid;

      // Create a batch to ensure all writes succeed or fail together
      const batch = writeBatch(db);

      // 1. Update the chat session with postflight completion
      const sessionRef = doc(db, `pilot/${pilotId}/chatSessions/${sessionId}`);
      batch.update(sessionRef, {
        'metadata.status': 'completed',
        postflightCompleted: true,
        completedAt: timestamp
      });

      // 2. Store detailed feedback in a separate collection
      const feedbackRef = doc(collection(db, `pilot/${pilotId}/feedback`));
      batch.set(feedbackRef, {
        sessionId: sessionId,
        flightDate: flightDate,
        submittedAt: timestamp,
        responses: responses,
        metadata: {
          riskAccuracy: responses.risk_accuracy,
          aiHelpfulness: responses.ai_helpfulness,
          unexpectedRisks: responses.encountered_risks === 'yes',
        },
        // Add any risks that were identified during the chat
        originalRisks: messages
          .filter(m => m.sender === 'assistant' && m.riskIdentified)
          .map(m => m.riskIdentified),
      });

      // 3. Update pilot's feedback stats
      const pilotRef = doc(db, 'pilot', pilotId);
      batch.update(pilotRef, {
        'stats.totalFlights': increment(1),
        'stats.lastFlightDate': flightDate,
        'stats.lastFeedbackDate': timestamp,
        [`stats.riskAccuracyDistribution.${responses.risk_accuracy}`]: increment(1)
      });

      // Commit all changes
      await batch.commit();
      console.log('Successfully saved postflight feedback:', feedbackRef.id);

      // Update UI
      setShowPostflight(false);
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: "Thank you for completing the post-flight questionnaire. Your feedback helps us improve our service.",
        sender: 'assistant',
        timestamp: timestamp
      }]);

    } catch (error) {
      console.error('Error saving postflight responses:', error);
      setError({
        message: 'Failed to save postflight responses',
        details: error.message
      });
    }
  };

  const handleSurveySubmit = async (surveyData) => {
    try {
      if (!auth.currentUser) return;

      const timestamp = new Date().toISOString();
      const pilotId = auth.currentUser.uid;

      // Save survey response
      const surveyRef = doc(collection(db, `pilot/${pilotId}/surveys`));
      await setDoc(surveyRef, {
        ...surveyData,
        submittedAt: timestamp,
        metadata: {
          platform: window.navigator.platform,
          userAgent: window.navigator.userAgent,
          language: window.navigator.language
        }
      });

      // Update pilot profile to hide CTA
      const pilotRef = doc(db, 'pilot', pilotId);
      await updateDoc(pilotRef, {
        'preferences.surveyCompleted': true,
        'preferences.lastSurveyDate': timestamp
      });

      setShowSurvey(false);
      
      // Show thank you message
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: "Thank you for your feedback! We appreciate your help in improving Aviation Advisor.",
        sender: 'assistant',
        timestamp: timestamp
      }]);

    } catch (error) {
      console.error('Error saving survey:', error);
      setError('Failed to save survey responses');
    }
  };

  useEffect(() => {
    chatService.setMessageUpdateCallback((content) => {
      setMessages(prev => prev.map(msg => 
        msg.isTyping ? { ...msg, text: content } : msg
      ));
    });

    return () => chatService.setMessageUpdateCallback(null);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-white flex flex-col">
      <SharedHeader />
      <SubNavigation />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showPostflight ? (
          <PostflightQuestionnaire
            flightDate={flightDate}
            onComplete={handlePostflightComplete}
            onSkip={() => setShowPostflight(false)}
          />
        ) : (
          <>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {error.message || error}
                  {error.details && (
                    <div className="mt-2 text-sm opacity-75">
                      Details: {error.details}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message, index) => (
                  <MessageContent 
                    key={`${message.id}-${index}`}
                    text={message.content || message.text} 
                    sender={message.role || message.sender}
                    userName={userProfile?.pilotName || 'User'}
                    isTyping={message.isTyping}
                    message={message}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="border-t p-4 bg-white">
                {/* Show question options if available and in questionnaire mode */}
                {isQuestionnaire && messages[messages.length - 1]?.options && (
                  <div className="flex justify-end space-x-2">
                    <div className="px-4 py-2 rounded-lg">
                      {messages[messages.length - 1].options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => handleAnswer(option)}
                          className="px-4 py-2 rounded-lg"
                          style={{
                            backgroundColor: 'hsla(158, 72%, 70%, 0.3)',
                            color: 'hsla(209, 37%, 31%, 1)',
                            borderRadius: '8px',
                            textAlign: 'right',
                            marginRight: '8px'
                          }}
                        >
                          {option.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div 
                  className={`flex w-full p-2 mt-4 justify-between items-center rounded-[6.25rem] gap-2
                    ${error 
                      ? 'border-[1.5px] border-sunrise-40 bg-surface-20 opacity-50'
                      : isRecording
                        ? 'border-[1.5px] border-sunrise-70 bg-sunrise-50'
                        : (!isQuestionnaire && (inputMessage || document.activeElement === document.querySelector('input')))
                          ? 'border-[1.5px] border-green-40 bg-neutral-white'
                          : 'border-[1.5px] bg-surface-20 opacity-50'}`}
                  onClick={() => console.log('Current state:', { isRecording, error, isQuestionnaire, inputMessage })}
                >
                  <div 
                    className={`flex w-8 p-[0.5625rem] justify-center items-center gap-2.5 rounded-2xl 
                      ${isRecording ? 'bg-sunrise-70' : 'bg-[#3F4140]'}`}
                    onClick={() => console.log('Mic button state:', { isRecording })}
                  >
                    <button
                      onClick={isRecording ? stopRecording : startRecording}
                      disabled={isQuestionnaire}
                      className={`flex w-full h-full justify-center items-center
                        ${isQuestionnaire ? 'cursor-not-allowed' : ''}`}
                    >
                      <FontAwesomeIcon 
                        icon={isRecording ? faStop : faMicrophone} 
                        className="text-white text-sm font-black leading-[0.875rem] fa-sharp"
                        style={{ fontFamily: '"Font Awesome 6 Sharp"' }}
                      />
                    </button>
                  </div>

                  {isRecording ? (
                    <>
                      <div className="flex items-center gap-2 px-3 py-1 bg-sunrise-40 rounded-full">
                        <span className="text-white text-sm font-medium">
                          {new Date(recordingTime * 1000).toISOString().substr(14, 5)}
                        </span>
                      </div>
                      
                      <div className="flex-1 h-8 flex items-center justify-start ml-2">
                        <div className="w-full h-full flex items-start justify-start gap-1">
                          {[...Array(20)].map((_, i) => (
                            <div
                              key={i}
                              className="waveform-bar w-1 bg-white rounded-full transition-all duration-100 justify-start"
                              style={{
                                height: `${Math.min(100, (audioLevel / 255) * 100 + Math.random() * 20)}%`,
                                opacity: 0.6 + Math.random() * 0.4,
                                animationDelay: `${i * 0.05}s`,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                boxShadow: '0 0 4px rgba(255, 255, 255, 0.5)'
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputMessage}
                        maxLength={1000}
                        disabled={isQuestionnaire}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder={isQuestionnaire ? "Please answer the questions above first" : "What can the Aviation Advisor help with today?"}
                        className={`flex-1 font-urbanist text-base font-medium h-6
                          ${(!isQuestionnaire && (inputMessage || document.activeElement === document.querySelector('input')))
                            ? 'text-gray-100 bg-neutral-white'
                            : 'text-gray-40 bg-surface-20'
                          }                      
                          placeholder:text-[#707271] placeholder:opacity-100 
                          focus:outline-none px-4
                          ${isQuestionnaire ? 'cursor-not-allowed' : ''}`}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !isQuestionnaire) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />

                      <div className="flex items-center mr-2">
                        <span className="font-urbanist text-[#707271] text-sm font-bold">
                          {inputMessage.length}/1000
                        </span>
                      </div>

                      <button
                        onClick={handleSpeakerToggle}
                        disabled={isQuestionnaire}
                        className={`p-2 rounded-full bg-gray-200 
                          ${isQuestionnaire ? 'cursor-not-allowed' : ''}`}
                      >
                        {isSpeaking ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                      </button>
                        
                      <div className="flex w-8 p-[0.5625rem] justify-center items-center gap-2.5 rounded-[12.4375rem] border-[1.4px] border-[#949595]">
                        <button
                          disabled={isQuestionnaire}
                          onClick={() => isQuestionnaire ? handleAnswer(inputMessage) : handleSendMessage()}
                          className={`flex w-full h-full justify-center items-center
                            ${isQuestionnaire ? 'cursor-not-allowed' : ''}`}
                        >
                          <FontAwesomeIcon 
                            icon={faGear} 
                            className="text-gray-60 text-sm leading-[0.875rem] fa-sharp"
                            style={{ fontFamily: '"Font Awesome 6 Sharp"' }}
                          />
                        </button>
                      </div>
                    </>
                  )}

                    <div className={`flex w-8 p-[0.5625rem] justify-center items-center gap-2.5 rounded-[6.25rem] bg-green-70 text-neutral-white
                    ${(isQuestionnaire || isRecording) ? 'opacity-80' : ''}`}>
                    <button
                      onClick={() => isQuestionnaire ? handleAnswer(inputMessage) : handleSendMessage()}
                      disabled={!inputMessage.trim() || isQuestionnaire || isRecording}
                      className={`flex w-full h-full justify-center items-center
                        ${(isQuestionnaire || isRecording) ? 'opacity-80 cursor-not-allowed' : ''}`}
                    >
                      <FontAwesomeIcon 
                        icon={faPaperPlane} 
                        className={`text-sm leading-[0.875rem] fa-sharp text-neutral-white
                          ${(isQuestionnaire || isRecording) ? ' opacity-80' : ''}`}
                        style={{ fontFamily: '"Font Awesome 6 Sharp"' }}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      
      <FeedbackCTA onClick={() => setShowSurvey(true)} />
      {showSurvey && (
        <UserSurvey
          onClose={() => setShowSurvey(false)}
          onSubmit={handleSurveySubmit}
        />
      )}
    </div>
  );
} 

export default EnhancedChatApp;